// BP 56 §2.1 — i18next instance + convention-based locale/namespace loading.
//
// Catalog files live at ./locales/<locale>/<ns>.json. This module DISCOVERS
// them via `import.meta.glob` (Vite rewrites the globs at build time) rather
// than a hand-maintained loader map, so a Wave-1 package only ever DROPS its
// JSON files — it never edits this file to register a new namespace. That is
// the concurrency-safety property the wave plan relies on (no shared loader
// registry = no contention between parallel extraction agents), AND it is what
// makes English's own extracted namespaces load at all (every en/*.json is
// bundled below, not just common/nav).
//
// English is bundled EAGERLY (every en namespace) so first paint is never a
// loading flash and the fallback language is always fully present; fr/es/da/de
// (and the dev-only qps pseudolocale) are Vite-code-split and fetched once on
// first switch to that locale, then cached in i18next's resource store.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, SUPPORTED_UI_LOCALES } from './constants';

// qps — the pseudolocale generated by `i18n-tools/pseudo.mjs` from the `en`
// catalogs (BP 56 §10/§11). Only activated when explicitly requested; a real
// user never sees it and `check-keys.mjs` never validates it.
export const PSEUDO_LOCALE = 'qps';
export const PSEUDO_ENABLED = import.meta.env.VITE_I18N_PSEUDO === '1';

// --- catalog discovery (build-time; Vite rewrites the globs) ----------------
// Eager: every English namespace, available synchronously at init (no flash,
// always-complete fallback language).
const EN_MODULES = import.meta.glob('./locales/en/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, unknown> }
>;
// Lazy: every real non-English namespace — one code-split loader per file.
// `en` is eager above; `qps` is excluded here and wired separately below, so a
// normal (non-pseudo) build never emits pseudolocale chunks.
const LAZY_MODULES = import.meta.glob([
  './locales/*/*.json',
  '!./locales/en/*.json',
  '!./locales/qps/*.json',
]) as Record<string, () => Promise<{ default: Record<string, unknown> }>>;

// qps loaders exist ONLY under the pseudo flag. Because PSEUDO_ENABLED is a
// compile-time constant, in a normal build this ternary's live branch is dead
// code and Rollup drops the qps dynamic imports entirely (no chunks shipped) —
// preserving WP-0A's verified "pseudo never reaches a real user" property.
const PSEUDO_MODULES: Record<string, () => Promise<{ default: Record<string, unknown> }>> =
  PSEUDO_ENABLED ? (import.meta.glob('./locales/qps/*.json') as Record<
    string,
    () => Promise<{ default: Record<string, unknown> }>
  >) : {};

// './locales/<locale>/<ns>.json' -> { locale, ns }
function parsePath(p: string): { locale: string; ns: string } {
  const m = /\/locales\/([^/]+)\/([^/]+)\.json$/.exec(p);
  return m ? { locale: m[1], ns: m[2] } : { locale: '', ns: '' };
}

// Assemble en resources + the full namespace inventory from what actually
// exists on disk (so Wave 1 growing the catalogs needs no edit here).
const EN_RESOURCES: Record<string, Record<string, unknown>> = {};
const ALL_NAMESPACES = new Set<string>();
for (const [p, mod] of Object.entries(EN_MODULES)) {
  const { ns } = parsePath(p);
  if (!ns) continue;
  EN_RESOURCES[ns] = mod.default;
  ALL_NAMESPACES.add(ns);
}

const loadedLocales = new Set<string>(['en']);
let initPromise: Promise<typeof i18n> | null = null;

/** Idempotent — safe to call from multiple modules; only the first call does work. */
export function initI18n(): Promise<typeof i18n> {
  if (!initPromise) {
    const supportedLngs: string[] = PSEUDO_ENABLED
      ? [...SUPPORTED_UI_LOCALES, PSEUDO_LOCALE]
      : [...SUPPORTED_UI_LOCALES];
    initPromise = i18n
      .use(initReactI18next)
      .init({
        lng: DEFAULT_LOCALE,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs,
        ns: [...ALL_NAMESPACES],
        defaultNS: 'common',
        resources: { en: EN_RESOURCES },
        interpolation: { escapeValue: false }, // React already escapes
        returnEmptyString: false,
        // Non-suspense: a consumer renders the fallback-language string
        // immediately and re-renders once a lazily-loaded catalog lands
        // (react-i18next default bindI18n), so no <Suspense> boundary is
        // required around ordinary components like AppShell.
        react: { useSuspense: false },
      })
      .then(() => i18n);
  }
  return initPromise;
}

/** Ensure a locale's catalogs are loaded into i18next (no-op if cached / `en`). */
async function ensureLoaded(locale: string): Promise<void> {
  if (loadedLocales.has(locale)) return;
  // qps only exists as a dev/QA artifact; never fetch it unless explicitly on.
  if (locale === PSEUDO_LOCALE && !PSEUDO_ENABLED) {
    loadedLocales.add(locale);
    return;
  }
  const source = locale === PSEUDO_LOCALE ? PSEUDO_MODULES : LAZY_MODULES;
  const entries = Object.entries(source).filter(([p]) => parsePath(p).locale === locale);
  await Promise.all(
    entries.map(async ([p, load]) => {
      const { ns } = parsePath(p);
      if (!ns) return;
      const mod = await load();
      i18n.addResourceBundle(locale, ns, mod.default, true, true);
    }),
  );
  loadedLocales.add(locale);
}

/** Load (if needed) and activate a locale. Accepts a plain `string` so the
 *  dev-only `qps` pseudolocale can flow through the same path. */
export async function applyLocale(locale: string): Promise<void> {
  await initI18n();
  await ensureLoaded(locale);
  if (i18n.language !== locale) await i18n.changeLanguage(locale);
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

export { i18n };

/** Current active locale for non-React call sites (formatDate.ts, format.ts). */
export function getActiveLocale(): string {
  return i18n.language || DEFAULT_LOCALE;
}
